/**
 * council's Talent Score (talent.talent_scores): 0-100 points from a points table per
 * work experience level and the band A-D those points give at the level. Whether someone is
 * "matchable" isn't shown (2026-10-22). Shown on the Talent profile page and the Approved / Active /
 * Matched lists only; matching doesn't read it. Labels shared by the profile card, the list cell and
 * the filter.
 */
import { alpha } from "@mui/material";

export const TALENT_SCORE_BANDS = ["A", "B", "C", "D"] as const;
/** Filter value for people with no band: not scored yet (e.g. no work experience level). */
export const TALENT_SCORE_NO_BAND = "none";

export const BAND_META: Record<string, { label: string; share: string; bg: string; fg: string }> = {
  A: { label: "A", share: "top 20%", bg: alpha("#059669", 0.14), fg: "#065F46" },
  B: { label: "B", share: "next 30%", bg: alpha("#2563EB", 0.12), fg: "#1E40AF" },
  C: { label: "C", share: "next 30%", bg: alpha("#D97706", 0.16), fg: "#92400E" },
  D: { label: "D", share: "bottom 20%", bg: alpha("#64748B", 0.16), fg: "#334155" },
};

/** Options for the band filter (value + label). */
export const TALENT_SCORE_FILTER_OPTIONS: { value: string; label: string }[] = [
  ...TALENT_SCORE_BANDS.map((b) => ({ value: b, label: `Band ${b} (${BAND_META[b].share} of the level)` })),
  { value: TALENT_SCORE_NO_BAND, label: "No band (not scored yet)" },
];

export function filterOptionLabel(value: string): string {
  return TALENT_SCORE_FILTER_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

const NOT_SCORED_LABEL: Record<string, string> = {
  no_level: "no work experience level",
};

/** Why someone has no score, from council's unscored reason. */
export function notScoredText(reason: string | null | undefined): string {
  if (!reason) return "no score yet";
  return NOT_SCORED_LABEL[reason] ?? reason.replace(/_/g, " ");
}

export const GROUP_LABEL: Record<string, string> = {
  lead: "Lead score",
  communication: "Communication",
  technical: "Technical",
  behavioral: "Behavioral",
  profile: "Profile readiness",
  clients: "Client history",
};

/** What each group is made of, for its hover. The weights per level live in council's config.js. */
export const GROUP_ABOUT: Record<string, string> = {
  lead: "The lead score (0-100) from the application.",
  communication:
    "The TA's Communication pick at the final round (1-4) counts most, then the assessment's communication " +
    "level (1-5), then the AI interview's transcript comms score (0-100).",
  technical:
    "The TA's technical interview rating (1-4) counts most, then the AI interview's technical and experience " +
    "scores (0-100). TA ratings from before Aug 2025 aren't used.",
  behavioral:
    "The final-round (behavioral) interview: the TA's Score on the final-round card (1 Strong No Hire, " +
    "2 Weak No Hire, 3 Hire, 4 Strong Hire). TA ratings from before Aug 2025 aren't used.",
  profile: "How complete the profile is: tribunal's readiness score (0-1000).",
  clients:
    "What clients did with this person on any role, best first: client interview feedback averaging 4.25+ " +
    "of 5, then 3.75+, then a client interview without scored feedback, then sent with no client interview " +
    "yet (or lower feedback), then never sent (neutral). An unsuccessful trial scores lowest. A send counts " +
    "from when the person enters Sent to client.",
};

const outOf = (scale: string) => (v: number | string) => `${typeof v === "number" ? points(v) : v} ${scale}`;

/**
 * Each council rule: its name, how to read its value, the group line's text when the input is missing,
 * and (`absent`, default "none yet") the hover's.
 */
const RULE_INFO: Record<string, { label: string; value: (v: number | string) => string; missing: string; absent?: string }> = {
  leadScore: { label: "Lead score", value: outOf("/ 100"), missing: "no lead score" },
  commsPick: { label: "TA Communication pick", value: outOf("of 4"), missing: "no TA Communication pick" },
  commsAssessment: { label: "Assessment communication level", value: outOf("of 5"), missing: "no assessment communication level" },
  commsAiTranscript: { label: "AI interview comms", value: outOf("/ 100"), missing: "no AI interview comms score" },
  techRating: { label: "TA technical rating", value: outOf("of 4"), missing: "no TA technical rating" },
  techAi: { label: "AI interview technical", value: outOf("/ 100"), missing: "no AI interview technical score" },
  expAi: { label: "AI interview experience", value: outOf("/ 100"), missing: "no AI interview experience score" },
  behRating: { label: "TA behavioral rating", value: outOf("of 4"), missing: "no TA behavioral rating" },
  profileReadiness: { label: "Profile readiness", value: outOf("/ 1000"), missing: "no profile readiness score" },
  clientRecord: {
    label: "Client record",
    value: (v) => clientRecordText(String(v)),
    missing: "never sent to a client",
    absent: "never sent to a client",
  },
};

/** council's notes on how an input was read. A note on a missing input replaces the "no …" text. */
const NOTE_TEXT: Record<string, string> = {
  rated_before_scale_change: "rated before Aug 2025, so not used",
  lead_score_unscorable: "couldn't be computed",
  gaap_cap_applied: "capped at 40 for limited US GAAP depth",
};

/** council's client record labels (engine.js clientRecordCredit). */
export function clientRecordText(value: string): string {
  const avg = /^feedback_avg_(.+)$/.exec(value);
  if (avg) return `client interview feedback averages ${avg[1]} of 5`;
  const known: Record<string, string> = {
    trial_unsuccessful: "a client trial was unsuccessful",
    interviewed_no_feedback: "had a client interview, no scored feedback",
    sent_no_interview: "sent to a client, no client interview yet",
    no_history: "never sent to a client",
  };
  return known[value] ?? value.replace(/_/g, " ");
}

export function ruleText(rule: string): string {
  return RULE_INFO[rule]?.label ?? rule;
}

export interface TalentScoreRuleLike {
  key: string;
  value: number | string | null;
  missing: boolean;
  note: string | null;
}

const noteText = (note: string | null) => (note ? NOTE_TEXT[note] ?? note.replace(/_/g, " ") : null);

/** Why a missing input is missing, in a few words (the group's "counted as neutral" line). */
export function missingText(rule: Pick<TalentScoreRuleLike, "key" | "note">): string {
  const note = noteText(rule.note);
  if (note) return `${ruleText(rule.key)}: ${note}`;
  return RULE_INFO[rule.key]?.missing ?? `no ${rule.key}`;
}

/** What council read for a rule, for the group's hover. */
export function ruleValueText(rule: TalentScoreRuleLike): string {
  if (rule.missing || rule.value == null) {
    return rule.note ? `${noteText(rule.note)}; counted as neutral` : `${RULE_INFO[rule.key]?.absent ?? "none yet"}, counted as neutral`;
  }
  const read = RULE_INFO[rule.key]?.value(rule.value) ?? String(rule.value);
  const note = noteText(rule.note);
  return note ? `${read} (${note})` : read;
}

/** "No x; no y (counted as neutral)", or null when nothing is missing. */
export function neutralLine(rules: Pick<TalentScoreRuleLike, "key" | "note" | "missing">[]): string | null {
  const texts = rules.filter((r) => r.missing).map(missingText);
  if (!texts.length) return null;
  const line = texts.join("; ");
  return `${line.charAt(0).toUpperCase()}${line.slice(1)} (counted as neutral)`;
}

export const TALENT_SCORE_ABOUT =
  "council's rules score: lead score, communication, technical and behavioral interviews, profile readiness " +
  "and client history, weighted per work experience level. Bands are per level: A is the top 20% of the " +
  "network at that level, B the next 30%, C the next 30%, D the bottom 20%. A missing input counts as neutral.";

/** One-decimal points without a trailing ".0". */
export function points(value: number): string {
  return String(Math.round(value * 10) / 10);
}
